import CourseThreadNotifications from "./components/CourseRealtime/CourseThreadNotifications";
import React, { useEffect } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import RootLayout from "./layouts/Root";
import ErrorPage from "./pages/error/Error";
import Calculation from "./pages/Dashboard/Calculation/Calculation";
import PaperInstruction from "./pages/Dashboard/PaperInstructions/PaperInstruction";
import AdditionalServices from "./pages/Dashboard/AdditionalServices/AdditionalServices";
import SignIn from "./pages/Login/SignIn";
import SignUp from "./pages/Login/SignUp";
import ForgotPassword from "./pages/Login/ForgotPassword";
import ResetPassword from "./pages/Login/ResetPassword";
import SecurePayment from "./pages/Dashboard/SecurePayment/SecurePayment"
import SuccessPayment from "./pages/Dashboard/SecurePayment/SuccessPayment"
import MyOrders from "./pages/Orders/MyOrders"
import PendingOrders from "./pages/Orders/PendingOrders"
import InOProgressOrders from "./pages/Orders/InProgressOrders";
import RaiseComplaint from "./pages/Orders/RaiseComplaintOrders";
import RaiseComplaintForm from "./pages/Orders/RaiseComplaintForm";
import Thread from "./pages/Thread/Thread";
import { WaitingPage } from "./pages/Login/WaitingPage";
import { PublicRoute, ProtectedRoute, OtpRouteGuard } from "./components/protectedAndPublicRoutex";
import RedirectToExternal from "./components/RedirectToExternal";
import Otp from "./pages/Login/Otp";
import { VerifyEmail } from "./pages/Login/VerifyEmail";
import { useSelector } from "react-redux";
import Profile from "./pages/Profile/Profile";
import MyWallet from "./pages/Wallet/MyWallet";
import PaymentErrorPage from "./pages/error/PaymentError";
import Dashboard from "./pages/Dashboard/Dashboard/Dashboard";
import ServerError from "./pages/error/ServerError";
import { UseConfirmationTesting } from "./pages/UseConfirmationTesting";
import CancelledOrders from "./pages/Orders/CancelledOrders";
import PrivacyPolicy from "./pages/Legal/PrivacyPolicy";
import TermsAndConditions from "./pages/Legal/TermsAndConditions";
import UnreadMessages from "./pages/Messages/UnreadMessages";
import PublicWriterProfile from "./pages/PublicWriterProfile/PublicWriterProfile";
import ReferralCapture from "./pages/Referral/ReferralCapture";
import ReferralDashboard from "./pages/Referral/ReferralDashboard";
import LoyaltyRewards from "./pages/Loyalty/LoyaltyRewards";
import { storeReferralCode } from "./utils/referralTracking";

import { coursesEnabled } from "./config/courseFeatures";
const MyCourses = React.lazy(() => import("./pages/Courses/MyCourses"));
const CourseRequest = React.lazy(() => import("./pages/Courses/CourseRequest"));
const CourseConversation = React.lazy(() => import("./pages/Courses/CourseConversation"));
const CoursePaymentReturn = React.lazy(() => import("./pages/Courses/CoursePaymentReturn"));
const CoursePayments = React.lazy(() => import("./pages/Courses/CoursePayments"));

const router = createBrowserRouter([
    {
        path: "/writer/:writerId",
        element: <PublicWriterProfile />,
        errorElement: <ErrorPage />,
    },
    {
        path: "/ref/:referralCode",
        element: <ReferralCapture />,
        errorElement: <ErrorPage />,
    },
    {
        path: "/privacy-policy",
        element: <PrivacyPolicy />,
        errorElement: <ErrorPage />,
    },
    {
        path: "/terms-and-conditions",
        element: <TermsAndConditions />,
        errorElement: <ErrorPage />,
    },

    /* ====================================> PROTECTED ROUTES <========================================= */
    {
        path: "/",
        element: <ProtectedRoute />,
        errorElement: <ErrorPage />,
        children: [
            {
                element: <RootLayout />,
                children: [
                    { index: true, element: <Dashboard initialCount={10} /> },
                    { path: "dashboard" , element: <Dashboard/>},
                    { path: "edit-profile", element: <Profile /> },
                    { path: "calculation", element: <Calculation /> },
                    { path: "paper-instructions", element: <PaperInstruction /> },
                    { path: "additional-services", element: <AdditionalServices /> },
                    { path: "secure-payment/*", element: <SecurePayment /> },
                    { path: "success-payment/*", element: <SuccessPayment /> },
                    { path: "my-orders", element: <MyOrders /> },
                    { path: "pending-orders", element: <PendingOrders/> },
                    { path: "in-progress", element: <InOProgressOrders/> },
                    { path: "cancelled-orders", element: <CancelledOrders/> },
                    { path: "raise-complaint", element: <RaiseComplaint /> },
                    { path: "raise-complaint-forms", element: <RaiseComplaintForm /> },
                    { path: "my-wallet", element: <MyWallet/> },
                    { path: "referrals", element: <ReferralDashboard /> },
                    { path: "loyalty-rewards", element: <LoyaltyRewards /> },
                    { path: "messages", element: <UnreadMessages /> },
                    ...(coursesEnabled ? [{
                        path: "courses",
                        element: <React.Suspense fallback={<p role="status">Loading courses…</p>}><MyCourses /></React.Suspense>,
                    }, {
                        path: "courses/new",
                        element: <React.Suspense fallback={<p role="status">Loading request form…</p>}><CourseRequest /></React.Suspense>,
                    }, {
                        path: "courses/:courseId/messages",
                        element: <React.Suspense fallback={<p role="status">Loading conversation…</p>}><CourseConversation /></React.Suspense>,
                    }, {
                        path: "courses/:courseId/payment-return",
                        element: <React.Suspense fallback={<p role="status">Checking payment…</p>}><CoursePaymentReturn /></React.Suspense>,
                    }, {
                        path: "courses/:courseId/payments",
                        element: <React.Suspense fallback={<p role="status">Loading payments…</p>}><CoursePayments /></React.Suspense>,
                    }] : []),

                ],
            },
        ],
    },
    {
        path: "/thread",
        element: <ProtectedRoute />,
        errorElement: <ErrorPage />,
        children: [{ path: "", element: <CourseThreadNotifications><Thread /></CourseThreadNotifications> }],
    },

    /* ========================================> PUBLIC ROUTES <================================================ */
    {
        path: "/",
        element: <PublicRoute />,
        errorElement: <ErrorPage />,
        children: [
            { index: true, element: <SignIn /> },
            { path: "signin", element: <SignIn /> },
            { path: "signup", element: <SignUp /> },
            { path: "forgot-password", element: <ForgotPassword /> },
            { path: "reset-password", element: <ResetPassword /> },
            { path: "google", element: <WaitingPage /> },
            { path: "verify-email", element: <VerifyEmail/>},
            {
                path: "/otp",
                element: <OtpRouteGuard />,
                children: [
                    { path: "", element: <Otp /> }  
                ]
            },
            {
                path: "contact-us",
                element: <RedirectToExternal uri={"https://coursepivot.com/contact/"}/>
            },
            {
                path: "about-us",
                element: <RedirectToExternal uri={"https://coursepivot.com/services/"}/>
            },
            {
                path: "server-error",
                element: <ServerError/>
            },
            {
                path: "use-confirm",
                element: <UseConfirmationTesting />
            },
        ],
    },
    {
        path: "/payment-error",
        element: <PaymentErrorPage />,
    },
]);

function App() {

    const user = useSelector((state)=> state.auth)
    useEffect(() => {
        const referralCode = new URLSearchParams(window.location.search).get("ref");
        if (referralCode) {
            storeReferralCode(referralCode);
        }
    },[user])

    return <RouterProvider router={router} />;
}

export default App;
